import React, { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@cms/ui/components/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@cms/ui/components/select";
import { Segmented } from "@cms/ui/components/segmented";
import { Badge } from "@cms/ui/components/badge";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell
} from "@cms/ui/components/table";
import type { ApiClient } from "@cms/portal-lib/api";
import { formatCount, formatTimestamp } from "@cms/portal-lib/format";
import type { AdminClient } from "../lib/outlet-context";

type SecurityRange = "24h" | "7d";

interface SecuritySummary {
  available: boolean;
  reason?: string;
  hosts: string[];
  totals?: {
    requests: number;
    firewallEvents: number;
    byAction: Array<{ action: string; count: number }>;
  };
  topCountries?: Array<{ country: string; count: number }>;
  topIps?: Array<{ ip: string; count: number }>;
  events?: Array<{
    datetime: string;
    action: string;
    source: string;
    clientIP: string;
    clientCountryName: string;
    clientRequestHTTPHost: string;
    clientRequestHTTPMethodName: string;
    clientRequestPath: string;
  }>;
}

// The zone plan retains ~8 days of adaptive analytics, so 7d is the ceiling.
const RANGE_OPTIONS: Array<{ value: SecurityRange; label: string }> = [
  { value: "24h", label: "24 hours" },
  { value: "7d", label: "7 days" },
];

export function SecurityPanel({ clients, api }: { clients: AdminClient[]; api: ApiClient | null }) {
  const [selectedId, setSelectedId] = useState("");
  const [range, setRange] = useState<SecurityRange>("7d");
  const [summary, setSummary] = useState<SecuritySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (tenantId: string, selectedRange: SecurityRange) => {
    if (!api) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.request<SecuritySummary>(
        `/admin/security/${tenantId}?range=${selectedRange}`
      );
      setSummary(result ?? null);
    } catch (err) {
      setSummary(null);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (selectedId) void load(selectedId, range);
  }, [selectedId, range, load]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Security monitoring</CardTitle>
        <CardDescription>
          Cloudflare traffic and firewall activity for the client's custom domains — blocked
          and challenged requests, top offenders, and the latest WAF events.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1 space-y-2">
            <label className="text-sm font-medium">Select client</label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a client..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} ({c.id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-64">
            <Segmented value={range} options={RANGE_OPTIONS} onChange={setRange} />
          </div>
        </div>

        {!api && (
          <p className="text-sm text-muted-foreground">
            Security monitoring requires a configured API. Set PUBLIC_API_BASE_URL to enable.
          </p>
        )}
        {error && <p className="text-sm text-destructive">Could not load security data: {error}</p>}
        {loading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading security data...
          </p>
        )}

        {!loading && !error && summary && !summary.available && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
            <ShieldAlert className="h-6 w-6 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">{summary.reason}</p>
          </div>
        )}

        {!loading && !error && summary?.available && <SecurityDetails summary={summary} />}
      </CardContent>
    </Card>
  );
}

function SecurityDetails({ summary }: { summary: SecuritySummary }) {
  const totals = summary.totals;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Requests" value={formatCount(totals?.requests ?? 0)} />
        <StatCard label="Firewall events" value={formatCount(totals?.firewallEvents ?? 0)} />
        <div className="rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">By action</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {(totals?.byAction ?? []).length === 0 && (
              <span className="text-sm text-muted-foreground">None in range</span>
            )}
            {(totals?.byAction ?? []).map((entry) => (
              <Badge key={entry.action} variant="secondary">
                {entry.action}: {formatCount(entry.count)}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <TopList title="Top countries" rows={(summary.topCountries ?? []).map((r) => [r.country || "Unknown", r.count])} />
        <TopList title="Top IPs" rows={(summary.topIps ?? []).map((r) => [r.ip, r.count])} />
      </div>

      <div>
        <p className="mb-2 text-sm font-medium">Recent firewall events</p>
        {(summary.events ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No firewall events in this range.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Request</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(summary.events ?? []).map((event, index) => (
                  <TableRow key={`${event.datetime}-${index}`}>
                    <TableCell className="whitespace-nowrap">{formatTimestamp(event.datetime)}</TableCell>
                    <TableCell><Badge variant="secondary">{event.action}</Badge></TableCell>
                    <TableCell>{event.source}</TableCell>
                    <TableCell className="font-mono text-xs">{event.clientIP}</TableCell>
                    <TableCell>{event.clientCountryName}</TableCell>
                    <TableCell className="max-w-64 truncate font-mono text-xs">
                      {event.clientRequestHTTPMethodName} {event.clientRequestHTTPHost}{event.clientRequestPath}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function TopList({ title, rows }: { title: string; rows: Array<[string, number]> }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{title}</p>
      {rows.length === 0 ? (
        <p className="mt-1 text-sm text-muted-foreground">None in range</p>
      ) : (
        <ul className="mt-1 space-y-1">
          {rows.map(([label, count]) => (
            <li key={label} className="flex items-center justify-between text-sm">
              <span className="truncate">{label}</span>
              <span className="ml-2 tabular-nums text-muted-foreground">{formatCount(count)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
